import {
  usePaginatedResource,
  useResourceItem,
  useCreateResource,
  useEditResource,
  useDeleteResource,
} from "./useCrudResource"

import {
  getAccessoriesLists,
  getAccessoriesList,
  createAccessoriesList,
  updateAccessoriesList,
  deleteAccessoriesList,
  getAllAccessoriesLists,
  getTrashedAccessoriesLists,
  restoreAccessoriesList,
  forceDeleteAccessoriesList,
  getAccessoriesListBySlug,
} from "api/accessoriesListsApi"

import {
  useIsFetching,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

export const ACCESSORIES_LIST_QUERY_KEY = "accessories-lists"

// -------------------- FETCH WITH STOCK INFO --------------------
export const useAccessoriesListsWithStock = (params = {}, enabled = true) => {
  return usePaginatedResource(
    [ACCESSORIES_LIST_QUERY_KEY, "with-stock"],
    getAccessoriesLists,
    params,
    enabled
  )
}

// -------------------- FETCH PAGINATED --------------------
export const useAccessoriesLists = (params = {}, enabled = true) => {
  return usePaginatedResource(
    ACCESSORIES_LIST_QUERY_KEY,
    getAccessoriesLists,
    params,
    enabled
  )
}

// -------------------- FETCH SINGLE ITEM --------------------
export const useAccessoriesList = (id, enabled = true) => {
  return useResourceItem(
    ACCESSORIES_LIST_QUERY_KEY,
    getAccessoriesList,
    id,
    enabled
  )
}

// -------------------- FETCH BY SLUG --------------------
export const useAccessoriesListBySlug = (slug, enabled = true) => {
  return useResourceItem(
    [ACCESSORIES_LIST_QUERY_KEY, "slug"],
    getAccessoriesListBySlug,
    slug,
    enabled
  )
}

// -------------------- CREATE --------------------
export const useCreateAccessoriesList = () => {
  return useCreateResource(
    createAccessoriesList,
    ACCESSORIES_LIST_QUERY_KEY
  )
}

// -------------------- EDIT --------------------
export const useEditAccessoriesList = () => {
  return useEditResource(
    updateAccessoriesList,
    ACCESSORIES_LIST_QUERY_KEY
  )
}

// -------------------- DELETE (Soft Delete) --------------------
export const useDeleteAccessoriesList = () => {
  return useDeleteResource(
    deleteAccessoriesList,
    ACCESSORIES_LIST_QUERY_KEY
  )
}

// -------------------- FORCE DELETE (Permanent Delete) --------------------
export const useForceDeleteAccessoriesList = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: forceDeleteAccessoriesList,
    onSuccess: (_, id) => {
      // Remove from trashed cache
      queryClient.setQueriesData(
        [ACCESSORIES_LIST_QUERY_KEY, "trashed"],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = updated.data.data.filter(
            item => item.id !== id
          )
          updated.data.total = Math.max(
            0,
            (updated.data.total || 1) - 1
          )
          return updated
        }
      )

      // Remove individual cache
      queryClient.removeQueries([ACCESSORIES_LIST_QUERY_KEY, id])
    },
  })
}

// -------------------- ADDITIONAL --------------------

// ✅ Get all (no pagination)
export const useAllAccessoriesLists = (params = {}) => {
  return usePaginatedResource(
    ACCESSORIES_LIST_QUERY_KEY,
    getAllAccessoriesLists,
    { ...params, all: true },
    true
  )
}

// ✅ Get trashed (soft-deleted)
export const useTrashedAccessoriesLists = (params = {}) => {
  return usePaginatedResource(
    [ACCESSORIES_LIST_QUERY_KEY, "trashed"],
    getTrashedAccessoriesLists,
    params,
    true
  )
}

// ✅ Restore trashed item
export const useRestoreAccessoriesList = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: restoreAccessoriesList,
    onSuccess: (res, id) => {
      const restoredItem = res?.data
      if (!restoredItem) return

      // Remove from trashed cache
      queryClient.setQueriesData(
        [ACCESSORIES_LIST_QUERY_KEY, "trashed"],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = updated.data.data.filter(
            item => item.id !== id
          )
          updated.data.total = Math.max(
            0,
            (updated.data.total || 1) - 1
          )
          return updated
        }
      )

      // Add to main list cache
      queryClient.setQueriesData(
        [ACCESSORIES_LIST_QUERY_KEY],
        old => {
          if (!old?.data?.data) return old
          const updated = structuredClone(old)
          updated.data.data = [restoredItem, ...updated.data.data]
          updated.data.total = (updated.data.total || 0) + 1
          return updated
        }
      )

      // Update individual cache
      queryClient.setQueryData(
        [ACCESSORIES_LIST_QUERY_KEY, id],
        restoredItem
      )
    },
  })
}

// -------------------- SEARCH (PROGRAMMATIC) --------------------
export const useAccessoriesListSearchAPI = ({
  all = true,
  limit = 5,
} = {}) => {
  const queryClient = useQueryClient()

  const accessoriesListSearchAPI = async searchTerm => {
    const queryKey = [
      "accessories-list-searching",
      { search: searchTerm, all, limit },
    ]

    const cached = queryClient.getQueryData(queryKey)
    if (cached) return cached

    const data = await queryClient.fetchQuery({
      queryKey,
      queryFn: () =>
        getAccessoriesLists({
          search: searchTerm,
          all: true,
        }),
    })

    return data
  }

  const loading =
    useIsFetching({
      queryKey: ["accessories-list-searching"],
    }) > 0

  return { accessoriesListSearchAPI, loading }
}
